/**
 * A Report's PDF, from images of its parts: the header, then each grid band
 * (a run of grid rows no component straddles). Bands are never split across
 * pages; they are packed onto US Letter landscape pages in order, and a band
 * taller than a page is scaled to fit one.
 * Pure but for pdf-lib, which loads only when a PDF is built.
 */

/** US Letter landscape, in PDF points (1/72 in). */
export const REPORT_PDF_PAGE = { width: 792, height: 612, margin: 36 };
/** Room under the content for the page number. */
const FOOTER = 18;
/** Space between two parts on a page. */
const GAP = 8;
const CONTENT_WIDTH = REPORT_PDF_PAGE.width - 2 * REPORT_PDF_PAGE.margin;
const CONTENT_HEIGHT =
  REPORT_PDF_PAGE.height - 2 * REPORT_PDF_PAGE.margin - FOOTER;

/** One captured part of the Report: a JPEG and its size in pixels. */
export type ReportPdfPart = { jpeg: Uint8Array; width: number; height: number };

/** A part as placed on its page, in points from the page's top-left corner. */
export type PlacedPart = {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Lays parts onto pages at the content width, in order: a part goes on the
 * current page when it fits beneath what is there, else starts a new one.
 */
export function paginateReportParts(
  parts: readonly Pick<ReportPdfPart, 'width' | 'height'>[],
): PlacedPart[][] {
  const pages: PlacedPart[][] = [];
  let page: PlacedPart[] = [];
  // Points of the page's content height taken so far.
  let used = 0;
  parts.forEach((part, index) => {
    let width = CONTENT_WIDTH;
    let height = (part.height * CONTENT_WIDTH) / part.width;
    if (height > CONTENT_HEIGHT) {
      width = (width * CONTENT_HEIGHT) / height;
      height = CONTENT_HEIGHT;
    }
    let top = page.length ? used + GAP : 0;
    if (top + height > CONTENT_HEIGHT) {
      pages.push(page);
      page = [];
      top = 0;
    }
    page.push({
      index,
      x: REPORT_PDF_PAGE.margin + (CONTENT_WIDTH - width) / 2,
      y: REPORT_PDF_PAGE.margin + top,
      width,
      height,
    });
    used = top + height;
  });
  if (page.length) pages.push(page);
  return pages;
}

/** The Report's PDF: its parts paginated, each page numbered "n / N". */
export async function buildReportPdf(
  parts: readonly ReportPdfPart[],
  title: string,
): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  pdf.setTitle(title);
  pdf.setCreator('Gently');
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const images = await Promise.all(
    parts.map((part) => pdf.embedJpg(part.jpeg)),
  );
  const pages = paginateReportParts(parts);
  pages.forEach((placed, number) => {
    const page = pdf.addPage([REPORT_PDF_PAGE.width, REPORT_PDF_PAGE.height]);
    for (const part of placed) {
      const image = images[part.index];
      if (!image) continue;
      // PDF coordinates run from the bottom-left corner.
      page.drawImage(image, {
        x: part.x,
        y: REPORT_PDF_PAGE.height - part.y - part.height,
        width: part.width,
        height: part.height,
      });
    }
    const label = `${number + 1} / ${pages.length}`;
    const size = 8;
    page.drawText(label, {
      x:
        REPORT_PDF_PAGE.width -
        REPORT_PDF_PAGE.margin -
        font.widthOfTextAtSize(label, size),
      y: REPORT_PDF_PAGE.margin - size,
      size,
      font,
      color: rgb(0.45, 0.45, 0.45),
    });
  });
  return pdf.save();
}

/** A filename for the Report's PDF: its title without characters filenames can't hold. */
export function reportPdfFileName(title: string): string {
  const base = title
    .replace(/[\\/:*?"<>|\p{Cc}]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${base || 'Report'}.pdf`;
}
